import dayjs from 'dayjs';
import {
  formatDate,
  formatFullFormOfAddress,
  formatFullHouseNumber,
  formatFullName,
  formatPhoneNumber,
} from './formatter';
import useInitStore from '../store/init';
import useOrderStore from '../store/order';
import type { PhoneNumber, SelectedArticle } from '../types/common';

const today = dayjs().format('DD.MM.YYYY');

// Maps store product names to the 6-2_Produkt radio-button export values in
// the PDF. The PDF export values differ from the displayed labels:
//   Glasfaser 50    -> 30
//   Glasfaser 150   -> 100
//   Glasfaser 300   -> 300
//   Glasfaser 600   -> 500
//   Glasfaser 1.000 -> 1000
const productList: Record<string, string> = {
  // 'Glasfaser 50': '/30', // not bookable, kept for reference only
  'Glasfaser 150': '/100',
  'Glasfaser 300': '/300',
  'Glasfaser 600': '/500',
  'Glasfaser 1000': '/1000',
};

const hardwareList: Record<string, string> = {
  'Eigenes Endgerät (nicht von EWE)': '/Eigenes',
  'FRITZ!Box 5630 Fiber': '/Basisbox',
  'FRITZ!Box 5690 Fiber': '/Premiumbox',
  'Ratenkauf FRITZ!Box 5630 Fiber': '/Basisbox',
  'Ratenkauf FRITZ!Box 5690 Fiber': '/Premiumbox',
};
// Die Kaufoption ist im Auftragsformular pro Box getrennt:
// _BB = Basisbox (5630), _PB = Premiumbox (5690)
const hardwareKaufoptionField: Record<string, string> = {
  'FRITZ!Box 5630 Fiber': '6-4_Hardware-Kaufoption_BB',
  'Ratenkauf FRITZ!Box 5630 Fiber': '6-4_Hardware-Kaufoption_BB',
  'FRITZ!Box 5690 Fiber': '6-4_Hardware-Kaufoption_PB',
  'Ratenkauf FRITZ!Box 5690 Fiber': '6-4_Hardware-Kaufoption_PB',
};
const hardwareListSellOption: Record<string, string> = {
  'FRITZ!Box 5630 Fiber': '/einmalig',
  'FRITZ!Box 5690 Fiber': '/einmalig',
  'Ratenkauf FRITZ!Box 5630 Fiber': '/Ratenkauf',
  'Ratenkauf FRITZ!Box 5690 Fiber': '/Ratenkauf',
};

const evnFields: Record<string, string> = {
  Nein: '/kein',
  'komplette Darstellung': '/ungekuerzt',
  'verkürzte Darstellung': '/gekuerzt',
};

const tbFields: Record<string, string> = {
  Nein: '/Off',
  'Standard (Name; Adresse; 1. Rufnummer)': '/standard',
  'gekürzt (Name; 1. Rufnummer)': '/gekuerzt',
};

function checkProductArticle(
  selections: SelectedArticle[],
  name: string,
  value?: string,
): '/Ja' | '/Off' {
  const isSelected = selections.some(
    item => item.name === name && (value === undefined || item.article.value === value),
  );
  return isSelected ? '/Ja' : '/Off';
}

export default function prepareProductContractData() {
  const {
    customerData,
    ordererData,
    ownerData,
    productData,
  } = useOrderStore();
  const { paramsAddress, currentUser } = useInitStore();

  const customerAddress = ordererData?.invoiceAddress ?? paramsAddress!;

  const {
    lastName: ordererLastName,
    firstName: ordererFirstName,
    dateOfBirth: ordererDateOfBirth,
    customerId: ordererCustomerId,
    isCompany: ordererIsCompany,
    companyName: ordererCompanyName,
  } = ordererData!.household;
  const {
    tel: ordererTel,
    mobile: ordererMobile,
    email: ordererEmail,
  } = ordererData!.contact;

  const firstOtherConnectionOwnerData = customerData!.otherConnectionOwnersData?.length
    ? customerData!.otherConnectionOwnersData[0]
    : null;

  const { selections } = productData!;

  const selectedProduct = selections.find(
    ({ name }) => name === 'selectedProduct',
  )!.article.value;
  const selectedHardware = selections.find(
    ({ name, article }) => name === 'selectedHardware' && article.term === 'Hardware',
  )!.article.value;
  const additionalReceivers = selections.filter(({ name }) => name === 'additionalReceiver');

  const ordererName = ordererIsCompany
    ? ordererCompanyName
    : formatFullFormOfAddress(ordererData!.household);

  let prefix = '';
  if (customerData!.portablePhoneNumbers?.length) {
    prefix = customerData!.portablePhoneNumbers.reduce((acc: string, number: PhoneNumber) => {
      if (number.prefix.length > 0 && number.number.length > 0) {
        return number.prefix === acc ? acc : '';
      }
      return acc;
    }, customerData!.portablePhoneNumbers[0]!.prefix);
  }

  let response: Record<string, unknown> = {
    // '_Auftragsnummer': '', unused
    '_Partner-Nr': currentUser!.vpNumber,
    _Bearbeiter: `${currentUser!.firstName} ${currentUser!.lastName}`,

    '1_Auftragsart': ordererCustomerId ? '/Aenderung' : '/neu',
    '1_Check_KdNr': ordererCustomerId ? '/Ja' : '/Off',
    '1_Kundennummer': ordererCustomerId || '',

    '2_Termin': '/asap',
    // '2_Datum': '',
    // '2_Datum-Umzug': '',
    // '2_Datum-Sperrung': '',
    // '2_Datum-Vertragsende': '',

    // '3_Anrede': `/${ordererSalutation}`, // field does not exist in the PDF
    '3_Name': ordererLastName,
    '3_Vorname': ordererFirstName,
    '3_Datum': ordererDateOfBirth ? formatDate(ordererDateOfBirth) : '',
    '3_Str': customerAddress.street,
    '3_Hausnummer': formatFullHouseNumber(customerAddress),
    '3_PLZ': customerAddress.zipCode,
    '3_Ort': customerAddress.city,
    '3_Kennwort': customerData!.customerPassword,

    '4_Name': formatFullName(ordererData!.household),
    '4_Telefon': ordererTel.prefix ? formatPhoneNumber(ordererTel) : '',
    '4_Mobil': ordererMobile.prefix ? formatPhoneNumber(ordererMobile) : '',
    '4_E-Mail': ordererEmail,

    '5-1_Str': paramsAddress!.street,
    '5-1_Hausnummer': formatFullHouseNumber(paramsAddress!),
    '5-1_PLZ': paramsAddress!.zipCode,
    '5-1_Ort': paramsAddress!.city,

    '5-3_ONT': '/installieren',
    // '5-3_LageONT': '',
    // '5-3_Home-ID': '',

    // '5-4_Vormieter': '',
    // '5-4_Telefon': '',
    // '5-3_Check_Anschluss': '',

    // eigentlich 6-1, aber Formular ist hier kaputt und produziert noch das Feld '6-'
    // '1_Check_Aktion': '/Off',
    // '6-1_Aktion': '',
    // '6-1_Check_Bonus': '/Off',
    // '6-1_Bonuscode': '',

    '6-2_Produkt': productList[selectedProduct],

    '6-3_Check_ZuhauseTV': checkProductArticle(selections, 'selectedTVOption', 'ZuhauseTV'),
    '6-3_Check_weitereReceiver': additionalReceivers.length > 0 ? '/Ja' : '/Off',
    '6-3_Anzahl': additionalReceivers.length > 0 ? additionalReceivers.length : '',
    '6-3_Check_DOKU': '/Off',
    '6-3_Check_FILM': '/Off',
    '6-3_Check_KINDER': '/Off',
    '6-3_Check_SPORT': '/Off',
    '6-3_Check_MEGAMIX': checkProductArticle(selections, 'themedPackages', 'Megamix'),
    '6-3_Check_Tuerkisch': checkProductArticle(selections, 'languagePackages', 'Türkisch'),
    '6-3_Check_Polnisch': checkProductArticle(selections, 'languagePackages', 'Polnisch'),
    '6-3_Check_Italienisch': checkProductArticle(selections, 'languagePackages', 'Italienisch'),
    '6-3_Check_Russisch': checkProductArticle(selections, 'languagePackages', 'Russisch'),
    '6-3_Check_Aufnahmespeicher': checkProductArticle(selections, 'recordingMemory'),
    '6-3_Check_Aufnahmespeicher-100': checkProductArticle(
      selections,
      'recordingMemory',
      'Aufnahmespeicher + 100 Stunden',
    ),
    '6-3_Check_Aufnahmespeicher-200': checkProductArticle(
      selections,
      'recordingMemory',
      'Aufnahmespeicher + 200 Stunden',
    ),
    '6-3_Check_Ausland1': checkProductArticle(
      selections,
      'selectedAbroadOption',
      'Auslandsflatrate 1',
    ),
    '6-3_Check_Ausland2': checkProductArticle(
      selections,
      'selectedAbroadOption',
      'Auslandsflatrate 2',
    ),
    '6-3_Check_Mobilfunkflatrate': checkProductArticle(
      selections,
      'selectedLandlineOption',
      'Mobilfunk-Flat',
    ),
    '6-3_Check_SIPA1': checkProductArticle(
      selections,
      'selectedSecurityPackage',
      'PC Sicherheitspaket (für 3 PCs)',
    ),
    '6-3_Check_SIPA2': checkProductArticle(
      selections,
      'selectedSecurityPackage',
      'PC Sicherheitspaket (für 3 PCs & 2 Android)',
    ),
    '6-4_Hardware': hardwareList[selectedHardware] ?? '/Off',
    '6-4_Hardware-Kaufoption_BB': '/Off',
    '6-4_Hardware-Kaufoption_PB': '/Off',

    // '6-5_Check_Sperre-019': '/Off',
    // '6-5_Check_Sperre-0900': '/Off',
    // '6-5_Check_Sperre-0137': '/Off',
    // '6-5_Check_Sperre-018': '/Off',
    // '6-5_Check_Sperre-012': '/Off',
    // '6-5_Check_Sperre-118': '/Off',
    // '6-5_Check_Sperre-Ausland': '/Off',
    // '6-5_Check_Sperre-R': '/Off',
    // '6-5_Check_Sperre-Sonstige': '/Off',

    '7-2_Name': customerData!.otherConnectionOwners && firstOtherConnectionOwnerData
      ? formatFullFormOfAddress(firstOtherConnectionOwnerData)
      : '',
    '7-2_Datum': today,

    // '7-3_Str': '',
    // '7-3_Hausnummer': '',
    // '7-3_PLZ': '',
    // '7-3_Ort': '',

    // '7-4_FN1': '',
    // '7-4_FN2': '',
    // '7-4_DSL': '',
    // '7-4_Andere': '',
    // '7-4_Andere2': '',

    '7-5_Check_Rufnummeruebernahme': customerData!.numberPortability ? '/Ja' : '/Off',
    '7-5_Vorwahl': prefix,

    '9-1_E-Mail': ordererEmail,
    '9-1_Check_Onlinerechnung': checkProductArticle(selections, 'invoiceDelivery', 'Online-Rechnung'),
    '9-1_Check_Postrechnung': checkProductArticle(selections, 'invoiceDelivery', 'Rechnung per Post'),

    '9-2_EVN': evnFields[customerData!.itemizedBill] || '',

    '10_Eintrag': customerData!.phoneBookEntry === 'Nein' ? '/kein' : '/ja',
    '10_Check_gedruckt': customerData!.phoneBookEntry === 'Nein' ? '/Off' : '/Ja',
    '10_Check_digital': customerData!.phoneBookEntry === 'Nein' ? '/Off' : '/Ja',
    '10_Check_Auskunft': customerData!.phoneBookEntry === 'Nein' ? '/Off' : '/Ja',
    '10_Eintragsart': tbFields[customerData!.phoneBookEntry] || '',

    '11_Rufnummeruebermittlung': '/ja',

    '13_Check_a': customerData!.telephoneAdvertisement ? 'Ja' : 'Off',
    // The following fields do not exist in the PDF and are therefore unused:
    // '14_Check_c': customerData!.extendedDataUsage ? '/Ja' : '/Off',
    // '14_Check_b': customerData!.telephoneAdvertisement ? '/Ja' : '/Off',
    // '16_3_Check': checkProductArticle(
    //   selections,
    //   'selectedPeriod',
    //   'Standard 24 Monats-Vertrag',
    // ),
    '16_Datum': today,
  };

  const kaufoptionField = hardwareKaufoptionField[selectedHardware];
  if (kaufoptionField) {
    response[kaufoptionField] = hardwareListSellOption[selectedHardware] ?? '/Off';
  }

  if (customerData!.selectedProvider) {
    response['7-1_Anschluss'] = '/vorhanden';
    response['7-1_Anbieter'] = customerData!.selectedProvider === 'Telekom' ? '/Telekom' : '/Anderer';
    response['7-1_Betreiber'] = customerData!.otherProvider || customerData!.selectedProvider;
  } else {
    response['7-1_Anschluss'] = '/neu';
    response['7-1_Anbieter'] = '/Off';
    response['7-1_Betreiber'] = '';
  }

  customerData!.portablePhoneNumbers?.forEach((element, index) => {
    response[`7-5_Check_Nummer-${index + 1}`] = '/Ja';
    response[`7-5_Nummer-${index + 1}`] = formatPhoneNumber(element);
  });

  if (ownerData?.isOwner) {
    const {
      isCompany: ownerIsCompany,
      companyName: ownerCompanyName,
    } = ownerData.household;

    const ownerName = ownerIsCompany
      ? ownerCompanyName
      : formatFullFormOfAddress(ownerData.household);

    if (ownerName !== ordererName) {
      // orderer is not the owner
      const {
        street,
        houseNumber,
        suffix,
        zipCode,
        city,
      } = ownerData.isClientsAddress ? paramsAddress! : ownerData.address!;

      response = {
        ...response,
        '5-2_Name': ownerName,
        '5-2_Str': street,
        '5-2_Hausnummer': formatFullHouseNumber({ houseNumber, suffix }),
        '5-2_PLZ': zipCode,
        '5-2_Ort': city,
      };
    }
  }

  if (!customerData!.wantsBankTransfer) {
    // SEPA mandate
    response = {
      ...response,
      SEPA_Name: ordererLastName,
      SEPA_Vorname: ordererFirstName,
      SEPA_Str: customerAddress.street,
      SEPA_Hausnummer: formatFullHouseNumber(customerAddress),
      SEPA_PLZ: customerAddress.zipCode,
      SEPA_Ort: customerAddress.city,
      SEPA_Kontoinhaber_Name: ordererLastName,
      SEPA_Kontoinhaber_Vorname: ordererFirstName,
      SEPA_IBAN: customerData!.iban,
      'SEPA_Ort-Datum': today,
    };
  }

  return JSON.stringify(response);
}
